/*
 * Google Ads tag (gtag.js) with Consent Mode v2. public/gtag-init.js loads the tag
 * on every page with ad and analytics storage denied, so Google can detect it but
 * no cookie is set; the CookieBanner choice updates that consent. The choice is
 * kept in localStorage so the banner shows once per browser.
 */

export const GOOGLE_ADS_ID = 'AW-18489455855';

/**
 * Label of the "Sign up" conversion action, from its event snippet in Google Ads
 * (send_to: 'AW-18489455855/<label>'). Null until it's set: sign-ups aren't reported.
 */
export const SIGNUP_CONVERSION_LABEL: string | null = null;

export const CONSENT_KEY = 'qamaster_cookie_consent';
export type Consent = 'granted' | 'denied';

/** Fired on window when the visitor's choice is cleared, so the banner shows again. */
export const CONSENT_RESET_EVENT = 'qamaster:consent-reset';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const getConsent = (): Consent | null => {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
};

const updateConsent = (value: Consent) => {
  window.gtag?.('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

export const setConsent = (value: Consent) => {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
  updateConsent(value);
};

/** Clears the choice and shows the banner again (footer "Cookie settings"). */
export const resetConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
  updateConsent('denied');
  window.dispatchEvent(new Event(CONSENT_RESET_EVENT));
};

/** Reports a completed sign-up once the label is set; Consent Mode decides what's stored. */
export const trackSignUp = () => {
  if (!window.gtag || !SIGNUP_CONVERSION_LABEL) return;
  window.gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${SIGNUP_CONVERSION_LABEL}` });
};
